import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useGovernance } from '../../context/GovernanceContext';

const EDGE_FN_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/audit-intake-agent`;

const SYSTEM_CAPTURED_PREFIX = 'SYSTEM_CAPTURED:';
const INTAKE_COMPLETE = 'INTAKE_COMPLETE';

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseAgentText(text) {
  const capturedSystems = [];
  let isComplete = false;
  const cleanLines = [];

  for (const line of text.split('\n')) {
    if (line.startsWith(SYSTEM_CAPTURED_PREFIX)) {
      try {
        const json = JSON.parse(line.slice(SYSTEM_CAPTURED_PREFIX.length).trim());
        capturedSystems.push(json);
      } catch {
        // malformed — skip
      }
    } else if (line.trim() === INTAKE_COMPLETE) {
      isComplete = true;
    } else {
      cleanLines.push(line);
    }
  }

  return { capturedSystems, isComplete, cleanText: cleanLines.join('\n').trim() };
}

// ── Sub-components ────────────────────────────────────────────────────────────

function ChatBubble({ message }) {
  const isUser = message.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      {!isUser && (
        <div className="w-7 h-7 rounded-full bg-brand-navy flex items-center justify-center text-white text-xs font-bold shrink-0 mr-2 mt-0.5">
          AI
        </div>
      )}
      <div
        className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? 'bg-brand-navy text-white rounded-tr-sm'
            : 'bg-gray-100 text-gray-800 rounded-tl-sm'
        }`}
      >
        {message.content}
      </div>
    </div>
  );
}

function SystemCard({ system, index }) {
  const typeLabels = {
    generative_ai: 'Generative AI',
    ml_model: 'ML Model',
    decision_support: 'Decision Support',
    automation: 'Automation',
    other: 'Other',
  };
  const autonomyLabels = {
    fully_automated: 'Fully Automated',
    human_in_loop: 'Human in Loop',
    human_on_loop: 'Human on Loop',
    advisory_only: 'Advisory Only',
  };
  const autonomyColour = {
    fully_automated: 'bg-red-100 text-red-700',
    human_in_loop: 'bg-yellow-100 text-yellow-700',
    human_on_loop: 'bg-blue-100 text-blue-700',
    advisory_only: 'bg-green-100 text-green-700',
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 mb-2">
      <div className="flex items-start justify-between gap-2">
        <div>
          <span className="text-xs font-bold text-brand-navy">#{index + 1}</span>
          <span className="ml-1.5 text-sm font-semibold text-gray-900">{system.name}</span>
        </div>
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${autonomyColour[system.decision_autonomy] || 'bg-gray-100 text-gray-600'}`}>
          {autonomyLabels[system.decision_autonomy] || system.decision_autonomy}
        </span>
      </div>
      {system.description && (
        <p className="text-xs text-gray-500 mt-1 leading-snug">{system.description}</p>
      )}
      <div className="flex flex-wrap gap-1 mt-2">
        {system.system_type && (
          <span className="text-[10px] bg-brand-navy/10 text-brand-navy px-1.5 py-0.5 rounded">
            {typeLabels[system.system_type] || system.system_type}
          </span>
        )}
        {system.vendor && (
          <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
            {system.vendor}
          </span>
        )}
        {system.deployment_status && (
          <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded capitalize">
            {system.deployment_status}
          </span>
        )}
      </div>
      {system.regulatory_exposure?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {system.regulatory_exposure.map((r) => (
            <span key={r} className="text-[10px] bg-orange-50 text-orange-700 px-1.5 py-0.5 rounded">
              {r}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function AuditIntakeAgent() {
  const { id: clientId } = useParams();
  const { clients } = useGovernance();
  const client = clients.find((c) => c.id === clientId);

  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]); // { role, content }
  const [capturedSystems, setCapturedSystems] = useState([]);
  const [isComplete, setIsComplete] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [thinking, setThinking] = useState(false);
  const [error, setError] = useState('');
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // ── Load or create session ──────────────────────────────────────────────────
  useEffect(() => {
    if (!clientId) return;

    async function loadSession() {
      setLoading(true);
      // Try to find an active session
      const { data: existing } = await supabase
        .from('intake_sessions')
        .select('*')
        .eq('client_id', clientId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (existing) {
        setSession(existing);
        const existingMessages = existing.messages || [];
        setMessages(existingMessages);
        // If session exists but agent never responded, kick it off
        if (existingMessages.length === 0) {
          await sendToAgent([], existing);
        }
      } else {
        // Create new session
        const { data: created } = await supabase
          .from('intake_sessions')
          .insert({ client_id: clientId, messages: [] })
          .select()
          .single();
        setSession(created);
        // Kick off the first agent message
        await sendToAgent([], created);
      }

      // Load already-captured systems
      const { data: systems } = await supabase
        .from('ai_systems')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: true });
      if (systems) setCapturedSystems(systems);

      setLoading(false);
    }

    loadSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // ── Scroll to bottom on new messages ───────────────────────────────────────
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, thinking]);

  // ── Call Edge Function ──────────────────────────────────────────────────────
  const sendToAgent = useCallback(async (history, currentSession) => {
    setThinking(true);
    setError('');

    try {
      const res = await fetch(EDGE_FN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: history }),
      });

      if (!res.ok) throw new Error(`Agent error: ${res.status}`);
      const data = await res.json();

      // Anthropic API error (e.g. missing/invalid API key)
      if (data.type === 'error') {
        throw new Error(`Anthropic API error: ${data.error?.message ?? 'Unknown error. Check that ANTHROPIC_API_KEY is set in Supabase Edge Function secrets.'}`);
      }

      const rawText = data.content?.[0]?.text ?? '';

      const { capturedSystems: newSystems, isComplete: done, cleanText } = parseAgentText(rawText);

      const agentMsg = { role: 'assistant', content: cleanText };
      const updatedMessages = [...history, agentMsg];
      setMessages(updatedMessages);

      // Save any newly captured systems to Supabase
      if (newSystems.length > 0) {
        const toInsert = newSystems.map((s) => ({ ...s, client_id: clientId }));
        const { data: inserted } = await supabase
          .from('ai_systems')
          .insert(toInsert)
          .select();
        if (inserted) setCapturedSystems((prev) => [...prev, ...inserted]);
      }

      // Update session in Supabase
      const sess = currentSession || session;
      if (sess) {
        const updates = {
          messages: updatedMessages,
          systems_identified: (capturedSystems.length + newSystems.length),
        };
        if (done) {
          updates.status = 'completed';
          updates.completed_at = new Date().toISOString();
          setIsComplete(true);
        }
        await supabase.from('intake_sessions').update(updates).eq('id', sess.id);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setThinking(false);
      inputRef.current?.focus();
    }
  }, [clientId, session, capturedSystems.length]);

  // ── Send a user message ─────────────────────────────────────────────────────
  const handleSend = async () => {
    const text = input.trim();
    if (!text || thinking || isComplete) return;

    const userMsg = { role: 'user', content: text };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput('');

    await sendToAgent(updatedMessages);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-mid-grey text-sm">
        Starting intake session…
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-56px)] overflow-hidden">

      {/* ── Chat panel ── */}
      <div className="flex flex-col flex-1 min-w-0">

        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <Link to={`/clients/${clientId}`} className="text-mid-grey hover:text-brand-navy text-sm transition-colors">
                ← {client?.name ?? 'Client'}
              </Link>
              <span className="text-gray-300">/</span>
              <span className="text-sm font-semibold text-brand-navy">AI System Intake</span>
            </div>
            {isComplete && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full mt-1">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                Intake complete
              </span>
            )}
          </div>
          <div className="text-xs text-mid-grey">
            {capturedSystems.length} system{capturedSystems.length !== 1 ? 's' : ''} captured
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-6 py-4 bg-gray-50">
          {messages.map((msg, i) => (
            <ChatBubble key={i} message={msg} />
          ))}
          {thinking && (
            <div className="flex justify-start mb-3">
              <div className="w-7 h-7 rounded-full bg-brand-navy flex items-center justify-center text-white text-xs font-bold shrink-0 mr-2 mt-0.5">
                AI
              </div>
              <div className="bg-gray-100 px-4 py-2.5 rounded-2xl rounded-tl-sm flex gap-1 items-center">
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          )}
          {error && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2 mb-3">
              {error}
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="bg-white border-t border-gray-200 px-4 py-3 shrink-0">
          {isComplete ? (
            <p className="text-sm text-center text-mid-grey py-1">
              Intake session complete. View captured systems in the panel →
            </p>
          ) : (
            <div className="flex gap-2">
              <textarea
                ref={inputRef}
                rows={2}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your response… (Enter to send, Shift+Enter for new line)"
                disabled={thinking}
                className="flex-1 resize-none border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand-navy disabled:opacity-50"
              />
              <button
                onClick={handleSend}
                disabled={thinking || !input.trim()}
                className="px-4 py-2 bg-brand-navy text-white rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-40 transition-opacity self-end"
              >
                Send
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Systems sidebar ── */}
      <div className="w-72 border-l border-gray-200 bg-white flex flex-col shrink-0">
        <div className="px-4 py-3 border-b border-gray-200 shrink-0">
          <h2 className="text-sm font-semibold text-brand-navy">Captured Systems</h2>
          <p className="text-xs text-mid-grey mt-0.5">Saved automatically as the agent confirms each system</p>
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {capturedSystems.length === 0 ? (
            <p className="text-xs text-mid-grey text-center mt-6">
              No systems captured yet. Answer the agent's questions to begin.
            </p>
          ) : (
            capturedSystems.map((s, i) => (
              <SystemCard key={s.id || i} system={s} index={i} />
            ))
          )}
        </div>
        {isComplete && capturedSystems.length > 0 && (
          <div className="p-3 border-t border-gray-200 shrink-0">
            <Link
              to={`/clients/${clientId}`}
              className="block w-full text-center px-4 py-2 bg-brand-navy text-white rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              View client dashboard →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
